import React from 'react';
interface UsageRateGraphProps {
  data: {
    day: string;
    actualGallons: number;
    doctrineGallons: number;
  }[];
  valueKey: string;
}
export const WaterUsageRateGraph: React.FC<UsageRateGraphProps> = ({
  data,
  valueKey
}) => {
  const width = 360;
  const height = 180;
  const padding = 40;
  // Parse and map data correctly - using actualGallons and doctrineGallons
  const mappedData = data.map(d => ({
    day: parseInt(d.day),
    actual: d.actualGallons,
    doctrine: d.doctrineGallons
  }));
  const xValues = mappedData.map(d => d.day);
  const yValues = mappedData.flatMap(d => [d.actual, d.doctrine]);
  // Calculate appropriate min/max Y values from the actual data
  const minY = 0;
  const maxY = Math.max(...yValues) * 1.1; // Add 10% padding
  const minX = Math.min(...xValues);
  const maxX = Math.max(...xValues);
  // Create appropriate Y-axis ticks based on the data range
  const yTickCount = 5;
  const yTickStep = Math.ceil(maxY / yTickCount / 100000) * 100000;
  const yTicks = Array.from({
    length: yTickCount
  }, (_, i) => i * yTickStep);
  const getX = (x: number) => maxX === minX ? padding : (x - minX) / (maxX - minX) * (width - 2 * padding) + padding;
  const getY = (y: number) => maxY === minY ? height - padding : height - padding - (y - minY) / (maxY - minY) * (height - 2 * padding);
  const actualPoints = mappedData.map(d => `${getX(d.day)},${getY(d.actual)}`).join(' ');
  const doctrinePoints = mappedData.map(d => `${getX(d.day)},${getY(d.doctrine)}`).join(' ');
  return <div className="bg-white p-4 border border-gray-200 rounded-md" data-id="element-959">
      <h3 className="text-sm font-semibold text-gray-700 mb-2" data-id="element-960">
        Daily Gallons vs Doctrine
      </h3>

      <div className="flex flex-row items-start space-x-4" data-id="element-961">
        <svg width={width} height={height} data-id="element-962">
          {/* Axes */}
          <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#ccc" data-id="element-963" />
          <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#ccc" data-id="element-964" />

          {/* Y-axis ticks */}
          {yTicks.map((tick, i) => {
          const y = getY(tick);
          return <g key={i} data-id="element-965">
                <line x1={padding - 4} x2={padding} y1={y} y2={y} stroke="#999" data-id="element-966" />
                <line x1={padding} x2={width - padding} y1={y} y2={y} stroke="#eee" data-id="element-967" />
                <text x={padding - 8} y={y + 4} fontSize="10" fill="#666" textAnchor="end" data-id="element-968">
                  {tick.toLocaleString()}
                </text>
              </g>;
        })}

          {/* Y-axis label */}
          <text x={padding - 30} y={padding + (height - 2 * padding) / 2} textAnchor="middle" fontSize="10" fill="#666" transform={`rotate(-90 ${padding - 30},${padding + (height - 2 * padding) / 2})`} data-id="element-969">
            Gallons / day
          </text>

          {/* X-axis ticks */}
          {mappedData.map((d, i) => <text key={`tick-${i}`} x={getX(d.day)} y={height - padding + 14} fontSize="10" fill="#666" textAnchor="middle" data-id="element-970">
              {d.day}
            </text>)}

          {/* X-axis label */}
          <text x={(padding + (width - padding)) / 2} y={height - padding + 28} textAnchor="middle" fontSize="10" fill="#666" data-id="element-971">
            Day (negative = days ago)
          </text>

          {/* Data lines */}
          <polyline points={doctrinePoints} fill="none" stroke="#bbb" strokeWidth={2} data-id="element-972" />
          <polyline points={actualPoints} fill="none" stroke="#000" strokeWidth={2} data-id="element-973" />

          {/* Data points */}
          {mappedData.map((d, i) => <circle key={`a-${i}`} cx={getX(d.day)} cy={getY(d.actual)} r={2} fill="#000" data-id="element-974" />)}
          {mappedData.map((d, i) => <circle key={`d-${i}`} cx={getX(d.day)} cy={getY(d.doctrine)} r={2} fill="#bbb" data-id="element-975" />)}
        </svg>

        {/* Annotation + Legend */}
        <div className="flex flex-col space-y-3 text-xs max-w-[280px]" data-id="element-976">
          <div className="text-amber-700 bg-amber-50 px-3 py-2 rounded-md border border-amber-200" data-id="element-977">
            <span className="font-medium" data-id="element-978">Water consumption rate</span> is
            trending ~2.2% above doctrine guidance, consider revising
            assumptions accordingly.
          </div>

          <div className="flex space-x-4 text-gray-600" data-id="element-979">
            <div className="flex items-center space-x-1" data-id="element-980">
              <div className="w-3 h-0.5 bg-black" data-id="element-981"></div>
              <span data-id="element-982">Actual</span>
            </div>
            <div className="flex items-center space-x-1" data-id="element-983">
              <div className="w-3 h-0.5 bg-gray-400" data-id="element-984"></div>
              <span data-id="element-985">Doctrine</span>
            </div>
          </div>
        </div>
      </div>
    </div>;
};